import React, { useEffect, useId, useState } from "react";
import apiClient from "../../api/apiClient";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";

const ENTITY_OPTIONS = [
  { value: "risk", label: "Risk" },
  { value: "control", label: "Control" },
  { value: "audit", label: "Audit" },
  { value: "incident", label: "Incident" },
  { value: "vendor", label: "Vendor" },
  { value: "policy", label: "Policy" },
  { value: "action", label: "Task/Action" },
];

async function fetchEntities(entityType, query) {
  const params = query ? { search: query, query } : {};
  switch (entityType) {
    case "risk":
      return apiClient.get("/risks/search", { params }).then((r) => r.data || []);
    case "control":
      return apiClient.get("/controls", { params }).then((r) => r.data || []);
    case "audit":
      return apiClient.get("/audits", { params }).then((r) => r.data || []);
    case "incident":
      return apiClient.get("/incidents", { params }).then((r) => r.data || []);
    case "vendor":
      return apiClient.get("/vendors", { params }).then((r) => r.data || []);
    case "policy":
      return apiClient.get("/tenant/policies", { params }).then((r) =>
        Array.isArray(r.data)
          ? r.data.map((item) => ({
              ...item,
              id: item?.tenant_policy_id ?? item?.id,
            }))
          : []
      );
    case "action":
      return apiClient.get("/actions", { params }).then((r) => r.data || []);
    default:
      return [];
  }
}

export default function KpiLinkModal({ open, onClose, onSelect }) {
  const [entityType, setEntityType] = useState("risk");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setQuery("");
  }, [open]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const data = await fetchEntities(entityType, query);
      setResults(Array.isArray(data.items) ? data.items : data);
    } catch (err) {
      console.error("Entity search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <ModalA11yWrapper open={open} onClose={onClose} labelledBy={titleId}>
      <div
        style={{
          background: "white",
          padding: "16px",
          width: "520px",
          borderRadius: "12px",
          boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h3 id={titleId} style={{ margin: 0, fontSize: "16px" }}>Link KPI to entity</h3>
          <Button
            onClick={onClose}
            variant="ghost"
            style={{ border: "none", background: "transparent", cursor: "pointer" }}
            aria-label="Close"
          >
            x
          </Button>
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px", alignItems: "flex-end" }}>
          <Select
            label="Entity type"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            style={{ padding: "8px", flex: "0 0 160px" }}
          >
            {ENTITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Input
            label="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or title"
            style={{ flex: 1, padding: "8px" }}
          />
          <Button
            onClick={handleSearch}
            variant="primary"
            style={{
              padding: "8px 12px",
              background: "#10b2aa",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>

        <div style={{ maxHeight: "260px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
          {results.map((item) => {
            const label = item.name || item.title || item.id;
            const description = item.description || item.status || "";
            return (
              <div
                key={item.id}
                style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid #f3f4f6",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
                role="button"
                tabIndex={0}
                onClick={() => {
                  onSelect({ entity_type: entityType, entity_id: String(item.id) });
                  onClose();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect({ entity_type: entityType, entity_id: String(item.id) });
                    onClose();
                  }
                }}
              >
                <div style={{ fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>{description}</div>
              </div>
            );
          })}
          {results.length === 0 && (
            <div style={{ padding: "16px", color: "#6b7280", fontSize: "13px" }}>
              {loading ? "Searching..." : "No results yet. Try a different search."}
            </div>
          )}
        </div>
      </div>
    </ModalA11yWrapper>
  );
}
